"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { AddStockDialog } from "../watchlist/_components/add-stock-dialog";

/**
 * New-user empty state for `/compass`. Shows when the signed-in user has
 * zero Active-tier watchlist stocks. Renders a sample brief card based on
 * NVDA-shaped content (real shape, but presented as a sample) and a CTA
 * that opens the existing watchlist add-stock dialog.
 */
export function EmptyStateNewUser() {
  const router = useRouter();
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-surface-light/30 bg-surface/40 p-8">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-serif text-foreground mb-2">
            Welcome to Compass
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Compass watches your conviction stocks every week and emails you a
            personalised brief. Add stocks to your Active tier to start
            getting weekly synthesis. Below is a sample brief.
          </p>
        </div>

        {/* Sample brief preview (anonymised NVDA-shape) */}
        <div className="rounded-xl border border-gold/30 bg-navy-mid/40 p-5 max-w-2xl relative">
          <div className="absolute -top-2.5 left-4 text-[10px] font-bold tracking-wider uppercase text-gold bg-navy px-2 py-0.5 rounded border border-gold/30">
            Sample
          </div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col">
              <span className="text-xs font-mono text-muted-foreground">
                NVDA · NASDAQ
              </span>
              <span className="text-base font-semibold text-foreground">
                NVIDIA Corporation
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-gold/15 text-gold px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">
                Accumulate
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-foreground">8</span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
            <div className="flex-1 h-1.5 rounded-full bg-surface-light/30 overflow-hidden">
              <div
                className="h-full bg-gold/70 rounded-full"
                style={{ width: "80%" }}
              />
            </div>
            <span className="text-[11px] font-semibold text-gold">+1</span>
          </div>

          <p className="text-sm font-semibold text-foreground mb-2">
            Datacenter demand keeps printing
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            Hyperscaler capex commitments through 2027 remain materially
            ahead of supply, and Blackwell ramp is shipping ahead of
            internal targets. Margin floor protected by 80%+ data-center
            mix; valuation premium justified by the durability of the
            cycle.
          </p>

          <div className="rounded-lg border border-surface-light/20 bg-navy/40 px-3 py-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Watch
            </span>
            <p className="text-xs text-foreground mt-0.5">
              China export-control follow-ons and any softening in CSP
              capex guidance during Q1 prints.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="px-4 py-2 rounded-md bg-blue text-white text-sm font-semibold hover:opacity-90 transition cursor-pointer border-none"
          >
            + Add real stocks to start
          </button>
          <span className="text-xs text-muted-foreground">
            Your stocks will appear here once added.
          </span>
        </div>
      </div>

      <AddStockDialog
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => router.refresh()}
      />
    </div>
  );
}
